import { takeTicket } from '../ticketActions';
import {
  getSelectedExpertForForForwardingDropdown,
  isSelectedTicketLiveChat,
  extractSimplrTagsForSelectedTicket,
  extractSelectedTicketFromState,
  isTagOnForwardActive,
  extractTestTicketStatusFromState,
  getRecommendedMLTagsForSelectedTicket,
  getMultiIntentClassification,
  isSpecialist,
  extractSelectedInboxTab,
  getTagHistoryOfSelectedTicket,
  getSimplrTaggingActive,
  extractSelectedTicketIdFromState,
} from '../../extractFromState';
import {
  tagTicketAsOutOfScope,
  addNoteToTicket,
  forwardConversationToExperts,
  hideConversation,
  setTaggingActive,
  setShowTools,
  setLoadingForMacroWizard,
  addNoteOnDecisionTreeOpened,
  setPartnerTags,
} from '../../actions';
import { updateTicketInQueue } from '../ticketActions';
import { returnToPreviousMacroWizardPath } from '../searchActions';
import { salesforceChatV2PostQuery } from '../../api';
import { updateSimplrCategories } from '../../api/tickets';
import { fetchPartnerTags } from '../../api/partnerTags';
import { trackSimplrTagSelected } from '../../analytics/ticketEventAnalytics';
import {
  oneBtnPopupCreator,
  ticketCategoryDismissConfig,
  ticketCategoryDismissAndHideConfig,
  openConfirmOutOfScopePopup,
  macroNotCopiedConfig,
  twoBtnPopupCreator,
} from '../../components/popups/ConfirmPopup/helper';
import { handleError } from 'services/ErrorService/ErrorService';
import { ESCALATION_REASON } from '../../constants/noteTypes';
import { SALESFORCE_CHAT_2 } from '../../constants/conversationTypes';
import { ErrorStrings } from '../../constants/fixedMessages';
import { showExpertReviewNotificationPopup } from '../../components/popups/ExpertReviewNotificationPopup';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import isEmpty from 'lodash/isEmpty';

export const setTagForTicket = (simplrTag) => async (dispatch, getState) => {
  const state = getState();
  const ticket = extractSelectedTicketFromState(state);
  const currentUser = state.user;
  if (!ticket) {
    return handleError({
      customMessage: 'Tagging action: Could not find information for ticket to tag.',
      showPopup: true,
    });
  }

  if (ticket.ticketId === state.selectedHistoricalTicketId) {
    return handleError({ error: ErrorStrings.CANNOT_SET_TAGS_HISTORICAL, showPopup: true });
  }

  const { categoryRules } = extractSimplrTagsForSelectedTicket(state);
  const tagOnForwardActive = isTagOnForwardActive(state);
  const selectedExpert = getSelectedExpertForForForwardingDropdown(state);
  const isTestStoreTicket = extractTestTicketStatusFromState(state);
  const selectedInboxTab = extractSelectedInboxTab(state);
  const taggingActive = getSimplrTaggingActive(state);
  const isAdmin = isSpecialist(state);
  const selectedTicketIsLiveChat = isSelectedTicketLiveChat(state);
  const { categories } = ticket;
  const suggestedCategory = (categories && categories[0]) || null;
  const isOutOfScope = !categoryRules || !categoryRules[simplrTag];

  try {
    let analyticsMessage = null;
    if (isOutOfScope) {
      // the ticket category cannot be handled by Simplr
      if (isAdmin) {
        await dispatch(updateTagsOnTicket(simplrTag, isOutOfScope));
        if (tagOnForwardActive) {
          // used in test tagging situations for specialists when we can't take a ticket
          analyticsMessage = 'Specialist categorized ticket as unmanagable by Simplr and hid ticket';
          oneBtnPopupCreator(ticketCategoryDismissAndHideConfig);
          dispatch(hideConversation(ticket.ticketId));
          dispatch(setShowTools(false));
          dispatch(setTaggingActive(false));
        } else {
          // specialist can take the ticket even when out of the scope
          analyticsMessage = 'Specialist categorized ticket as out of scope';
          if (taggingActive) {
            analyticsMessage = 'Specialist categorized and took the out of the scope ticket';
            dispatch(setLoadingForMacroWizard(true));
            await dispatch(takeTicket(ticket.ticketId, ticket.simplrAccountPtrId, isAdmin, selectedInboxTab));
            dispatch(setLoadingForMacroWizard(false));
          }
          dispatch(setTaggingActive(false));
        }
      } else {
        if (isTestStoreTicket) {
          openConfirmOutOfScopePopup({
            content: {
              main: `You are about to escalate this ticket. Are you sure about your selection?`,
              iconPath: '/images/green-alert-bell.svg',
            },
            btnConfig: {
              dismiss: 'No, Go Back',
              confirm: 'Yes, Escalate',
            },
            onConfirm: async () => {
              await dispatch(updateTagsOnTicket(simplrTag, isOutOfScope));
              analyticsMessage = 'Expert categorized test ticket as unmanagable by Simplr the ticket was hidden';
              await dispatch(hideConversation(ticket.ticketId));
              oneBtnPopupCreator(ticketCategoryDismissConfig);
              dispatch(setTaggingActive(false));
            },
            onDismiss: () => {
              dispatch(returnToPreviousMacroWizardPath());
            },
          });
        } else {
          // used in normal tagging process for experts when we can't take a ticket
          if (taggingActive && !selectedTicketIsLiveChat) {
            // only email tickets during tagging
            analyticsMessage = 'Expert categorized ticket as unmanagable by Simplr';
            dispatch(handleOutOfScopeSelection(simplrTag));
          } else {
            openConfirmOutOfScopePopup({
              content: {
                main: `You are about to escalate this ticket. Are you sure about your selection?`,
                iconPath: '/images/green-alert-bell.svg',
              },
              btnConfig: {
                dismiss: 'No, Go Back',
                confirm: 'Yes, Escalate',
              },
              onConfirm: async () => {
                analyticsMessage = 'Expert categorized ticket as unmanagable by Simplr';
                dispatch(handleOutOfScopeSelection(simplrTag));
              },
              onDismiss: () => {
                dispatch(returnToPreviousMacroWizardPath());
              },
            });
          }
        }
      }
    } else {
      // in scope
      const tagHistoryBeforeUpdate = getTagHistoryOfSelectedTicket(state);
      await dispatch(updateTagsOnTicket(simplrTag, isOutOfScope));
      // the ticket category can be handled by Simplr
      if (isAdmin && tagOnForwardActive) {
        // used in test tagging situations for specialists when we can take a ticket
        analyticsMessage = 'Specialist categorized and forwarded ticket to expert';
        dispatch(forwardConversationToExperts(ticket.ticketId, selectedExpert, 'allForwardBtn', selectedInboxTab));
        dispatch(setShowTools(false));
        dispatch(setTaggingActive(false));
      } else {
        // used in normal tagging process for experts when we can take a ticket
        analyticsMessage = 'Expert categorized ticket as in scope';
        // set tagging false before taking ticket so paths do not get reset
        dispatch(setTaggingActive(false));
        if (taggingActive) {
          dispatch(setLoadingForMacroWizard(true));
          analyticsMessage = 'Expert categorized and took ticket';
          await dispatch(takeTicket(ticket.ticketId, ticket.simplrAccountPtrId, isAdmin, selectedInboxTab));
          dispatch(setLoadingForMacroWizard(false));
        }
      }

      if (!isAdmin) {
        dispatch(addNoteOnDecisionTreeOpened(ticket.ticketId, simplrTag, tagHistoryBeforeUpdate));
      }

      // Show expert review popup notification
      if (!currentUser.expertReviewPopupAcknowledged && ticket.escalatedForReview) {
        showExpertReviewNotificationPopup(currentUser.id);
      }
    }

    await dispatch(updatePartnerTags(ticket, simplrTag, selectedTicketIsLiveChat));

    SessionAnalyticsService.event(analyticsMessage, {
      ticket: ticket.ticketId,
      mlCategory: suggestedCategory,
      simplrTag,
    });
  } catch (err) {
    handleError({ error: err, showPopup: true });
  }
};

const updatePartnerTags = (ticket, simplrTag, selectedTicketIsLiveChat) => async (dispatch) => {
  if (selectedTicketIsLiveChat && ticket.ticketType === SALESFORCE_CHAT_2) {
    // no await because no need to block DTree work
    updateSalesforceChat2Tags(ticket, simplrTag);
  }
};

const handleOutOfScopeSelection = (simplrTag) => async (dispatch, getState) => {
  const ticket = extractSelectedTicketFromState(getState());
  await dispatch(updateTagsOnTicket(simplrTag, true));
  const outOfScopeMessage = `Escalation Reason: The expert categorized this ticket as "${simplrTag}". We do not take "${simplrTag}" tickets for this partner.`;
  await Promise.all([
    dispatch(addNoteToTicket(outOfScopeMessage, ticket, ESCALATION_REASON)),
    dispatch(tagTicketAsOutOfScope(ticket, () => oneBtnPopupCreator(ticketCategoryDismissConfig))),
  ]);
  dispatch(setTaggingActive(false));
};

const updateTagsOnTicket = (simplrTag, isOutOfScope) => async (dispatch, getState) => {
  const state = getState();
  const ticket = extractSelectedTicketFromState(state);
  if (!ticket) {
    return handleError({ error: ErrorStrings.SELECTED_TICKET_NOT_FOUND, showPopup: true });
  }

  const isAdmin = isSpecialist(state);
  const selectedInboxTab = extractSelectedInboxTab(state);
  const simplrCategories = simplrTag ? [simplrTag] : undefined;
  const updatedTicket = await updateSimplrCategories(simplrTag, ticket.ticketId, isAdmin, isOutOfScope);
  const recommendedMLTags = getRecommendedMLTagsForSelectedTicket(state);
  const multiIntentClassification = getMultiIntentClassification(state);

  trackSimplrTagSelected(simplrTag, recommendedMLTags, multiIntentClassification);
  updateTicketInQueue(updatedTicket, selectedInboxTab, isAdmin, simplrCategories);
};

const handleOutOfScopeChatMacroRecommendation = (ticket, tags) => async (dispatch) => {
  try {
    const escalationNote = `Expert selected out of scope chat macro recommendation. Tags: ${JSON.stringify(tags)}`;
    await Promise.all([
      dispatch(addNoteToTicket(escalationNote, ticket, ESCALATION_REASON)),
      dispatch(tagTicketAsOutOfScope(ticket, () => oneBtnPopupCreator(ticketCategoryDismissConfig))),
    ]);
  } catch (error) {
    return handleError({ error, customMessage: 'There was a problem escalating this ticket', showPopup: true });
  }
};

/**
 * Checks if any of the tags associated with chat macro recommendation are out of scope
 * Then it either executes copyFunction callback or displays a warning popup
 * @param {array} simplrTags
 * @param {function}
 * @return {function}
 */
export const handleUseChatMacroRecommendation = (chatMacro, copyFunction) => (dispatch, getState) => {
  const state = getState();
  const ticket = extractSelectedTicketFromState(state);
  const isAdmin = isSpecialist(state);
  const { tags } = chatMacro;

  const { categoryRules } = extractSimplrTagsForSelectedTicket(state);
  if (tags.length) {
    // RULE: if any of the tags are out of scope, the macro is out of scope
    const isOutOfScope = tags.some((simplrTag) => !categoryRules[simplrTag]);
    if (!isOutOfScope) {
      return copyFunction();
    } else if (isAdmin) {
      // specialists can always take out of scope tickets
      return twoBtnPopupCreator({
        content: {
          main: `You are about use a macro that is tagged as out of scope. Are you sure about your selection?`,
          iconPath: '/images/green-alert-bell.svg',
        },
        btnConfig: {
          dismiss: `No, don't use macro`,
          confirm: 'Yes, use macro',
        },
        onConfirm: () => copyFunction(),
        onDismiss: () => {
          // show popup saying text was not copied
          return oneBtnPopupCreator(macroNotCopiedConfig);
        },
      });
    } else if (!isAdmin) {
      return twoBtnPopupCreator({
        content: {
          main: `You are about to escalate this ticket. Are you sure about your selection?`,
          iconPath: '/images/green-alert-bell.svg',
        },
        btnConfig: {
          dismiss: "No, don't use macro",
          confirm: 'Yes, escalate',
        },
        onConfirm: async () => {
          dispatch(handleOutOfScopeChatMacroRecommendation(ticket, tags));
        },
        onDismiss: () => {
          // show popup saying text was not copied
          return oneBtnPopupCreator(macroNotCopiedConfig);
        },
      });
    }
  } else {
    return copyFunction();
  }
};

const updateSalesforceChat2Tags = async (ticket, simplrTag) => {
  if (!simplrTag) {
    const errorMsg = `Error updating platform Tags! Simplr tag "${simplrTag}" was not found for the ticket ${ticket.ticketId}`;
    handleError({ customMessage: errorMsg, showPopup: true });

    return null;
  }
  const caseId = ticket.moreTicketInfo && ticket.moreTicketInfo.caseId;
  if (!caseId) {
    const errorMsg = `Error updating platform Tags! Salesforce Case was not created for the ticket ${ticket.ticketId}`;
    handleError({ customMessage: errorMsg, showPopup: true });

    return null;
  }

  const payload = {
    simplrAccountId: ticket.simplrAccountPtrId,
    tag: simplrTag,
    caseId,
    conversationId: ticket.ticketId,
  };
  try {
    await salesforceChatV2PostQuery('/tags', payload);
  } catch (error) {
    handleError({ error, customMessage: `Error updating platform Tag ${simplrTag} for ticket ${ticket.ticketId}` });
  }
};

export const getPartnerTags = (ticket) => async (dispatch, getState) => {
  const params = {
    ticketId: ticket.ticketId,
  };

  const tags = await fetchPartnerTags(params);

  const currentTicketId = extractSelectedTicketIdFromState(getState());
  if (ticket.ticketId === currentTicketId && !isEmpty(tags)) {
    dispatch(setPartnerTags(tags));
  }
};
